import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Progress,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toReadableNumber } from '@partiu/ui/formatters.ts';

export function ExportConfirmationDialog({
  amount,
  modelName,
  onConfirm,
  ...props
}: IExportConfirmationDialogProps & UseDisclosureReturn) {
  const cancelRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const maxExport = 100000;
  const canExport = useMemo(() => amount < maxExport, [amount]);

  useEffect(() => {
    if (!props.isOpen) setIsExporting(false);
  }, [props.isOpen]);

  return (
    <AlertDialog
      closeOnOverlayClick={!isExporting}
      leastDestructiveRef={cancelRef}
      motionPreset="slideInBottom"
      {...props}
      isCentered
    >
      <AlertDialogOverlay transition={'1s'} backdropFilter={isExporting ? 'blur(10px)' : 'blur(5px)'} />

      {!canExport && (
        <AlertDialogContent>
          <AlertDialogHeader>Não é possível exportar esses dados</AlertDialogHeader>
          <AlertDialogBody>
            A quantidade de dados extrapola o máximo de {toReadableNumber(maxExport)}. Para conseguir uma tabela com
            estes dados por favor entre em contato em: suporte@partiu.com.br
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} variant="ghost" onClick={props.onClose}>
              Voltar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}

      {!isExporting && canExport && (
        <AlertDialogContent>
          <AlertDialogHeader>
            Exportar {toReadableNumber(amount)} {modelName}?
          </AlertDialogHeader>
          <AlertDialogBody>
            Esse processo poderá demorar diversos minutos e não pode ser interrompido. Caso muitas tentativas sem
            sucesso sejam feitas seu acesso pode ser cortado. Tem certeza que deseja exportar?
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} variant="ghost" onClick={props.onClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                onConfirm();
                setIsExporting(true);
              }}
              colorScheme="green"
              ml={3}
            >
              Exportar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}

      {isExporting && (
        <AlertDialogContent>
          <AlertDialogHeader>Exportando...</AlertDialogHeader>
          <AlertDialogBody paddingBottom={'25px'}>
            <Progress width={'100%'} height={'10px'} isIndeterminate />
          </AlertDialogBody>
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
}

interface IExportConfirmationDialogProps {
  amount: number;
  modelName: string;
  onConfirm: () => void;
}
